import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const INQUIRY_TYPES = [
  'オフィスIT機器・ネットワーク構築について',
  '情報セキュリティ対策について',
  'IT・DX支援について',
  'その他',
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      alert('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }
    setStatus('submitting');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d751r0n5hic0eqh31djg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-[#F8FCFF] text-[#1A2E3A]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative h-64 flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-[#163347]" />
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{ background: 'repeating-linear-gradient(45deg, #2B8DB8 0px, #2B8DB8 1px, transparent 1px, transparent 40px)' }} />
        </div>
        <div className="relative z-10 w-full px-10 lg:px-20 pb-12">
          <p className="text-[#7EC8E8] text-xs tracking-[0.4em] mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>CONTACT</p>
          <h1 className="text-white text-3xl lg:text-5xl font-light" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
            お問い合わせ
          </h1>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="px-10 lg:px-20 py-4 flex items-center gap-2 text-xs text-[#5A7A8A] tracking-wider border-b border-[#C8E4F2]">
        <Link to="/" className="hover:text-[#2B8DB8] transition-colors cursor-pointer">ホーム</Link>
        <i className="ri-arrow-right-s-line" />
        <span>お問い合わせ</span>
      </div>

      <section className="py-20 px-10 lg:px-20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">

          {/* Left: Info */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div>
              <p className="text-[#2B8DB8] text-xs tracking-[0.4em] mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>GET IN TOUCH</p>
              <h2 className="text-2xl font-light text-[#1A2E3A] leading-snug mb-4" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
                まずはお気軽に<br />ご相談ください
              </h2>
              <p className="text-[#5A7A8A] text-sm leading-loose">
                「何から始めればいいかわからない」という段階でも大丈夫です。お客様の状況をお聞きし、最適なご提案をいたします。
              </p>
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#DFF0FB] flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line text-[#2B8DB8] text-base" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#1A2E3A] mb-1 tracking-wider">営業所</p>
                  <p className="text-sm text-[#5A7A8A] leading-loose">神奈川県大和市南林間２－１８－４</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#DFF0FB] flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-[#2B8DB8] text-base" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#1A2E3A] mb-1 tracking-wider">受付時間</p>
                  <p className="text-sm text-[#5A7A8A] leading-loose">平日 9:00 〜 18:00</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#DFF0FB] flex-shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-[#2B8DB8] text-base" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#1A2E3A] mb-1 tracking-wider">無料相談</p>
                  <p className="text-sm text-[#5A7A8A] leading-loose">初回のご相談は無料です。<br />お気軽にどうぞ。</p>
                </div>
              </div>
            </div>

            <div className="border-t border-[#C8E4F2] pt-6">
              <p className="text-xs text-[#5A7A8A] tracking-wider mb-3">対応サービス</p>
              <div className="flex flex-col gap-2">
                {[
                  'オフィスIT機器・ネットワーク構築',
                  '情報セキュリティ対策',
                  'IT・DX支援',
                ].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2B8DB8] flex-shrink-0" />
                    <span className="text-xs text-[#1A2E3A] tracking-wider">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20 bg-white rounded-2xl border border-[#C8E4F2] px-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#DFF0FB] mb-6">
                  <i className="ri-checkbox-circle-line text-3xl text-[#2B8DB8]" />
                </div>
                <h3 className="text-xl font-light text-[#1A2E3A] mb-3" style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}>
                  送信が完了しました
                </h3>
                <p className="text-sm text-[#5A7A8A] leading-loose mb-8">
                  お問い合わせありがとうございます。<br />
                  内容を確認後、担当者よりご連絡いたします。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-8 py-3 bg-[#2B8DB8] text-white text-xs tracking-widest rounded-full hover:bg-[#1E6E94] transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  別の内容を送る
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl border border-[#C8E4F2] p-8 flex flex-col gap-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">
                      会社名 <span className="text-[#2B8DB8]">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      required
                      placeholder="株式会社〇〇"
                      className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">
                      お名前 <span className="text-[#2B8DB8]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">
                      メールアドレス <span className="text-[#2B8DB8]">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@company.co.jp"
                      className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="000-0000-0000"
                      className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF]"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">
                    お問い合わせ種別 <span className="text-[#2B8DB8]">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="inquiry_type"
                      required
                      className="w-full border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF] appearance-none cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      {INQUIRY_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-[#5A7A8A] pointer-events-none" />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#1A2E3A] tracking-wider">
                      お問い合わせ内容 <span className="text-[#2B8DB8]">*</span>
                    </label>
                    <span className={`text-xs ${charCount > 500 ? 'text-red-400' : 'text-[#5A7A8A]'}`}>
                      {charCount} / 500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="お問い合わせ内容をご記入ください。&#13;&#10;例：現在のセキュリティ対策が不十分か確認したい、PC環境を見直したい、など"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="border border-[#C8E4F2] rounded-lg px-4 py-3 text-sm text-[#1A2E3A] placeholder-[#B0C8D4] focus:outline-none focus:border-[#2B8DB8] transition-colors bg-[#F8FCFF] resize-none"
                  />
                </div>

                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-400 text-xs bg-red-50 px-4 py-3 rounded-lg">
                    <i className="ri-error-warning-line" />
                    送信に失敗しました。時間をおいて再度お試しください。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting' || charCount > 500}
                  className="w-full py-4 bg-[#2B8DB8] text-white text-sm tracking-widest rounded-lg hover:bg-[#1E6E94] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      送信中...
                    </>
                  ) : (
                    <>
                      送信する
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>

                <p className="text-[#5A7A8A] text-xs text-center leading-loose">
                  送信いただいた情報はお問い合わせへの回答にのみ使用します。<br />
                  <span className="text-[#B0C8D4]">* は必須項目です</span>
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
